/**
 * Shared Medplum-backed hook for the Client Profile screen.
 *
 * Loads Patient, managing Organization, contacts (Patient.contact
 * + RelatedPerson), and billing data (payment methods, Coverage,
 * CoverageEligibilityResponse, Invoices/Encounters, Statements).
 *
 * Returns a single object consumed by all tabs so each tab need not refetch.
 */

import { useMedplum } from '@medplum/react';
import type {
  Basic,
  Consent,
  Coverage,
  CoverageEligibilityResponse,
  Organization,
  Patient,
  RelatedPerson,
  Appointment,
  EpisodeOfCare,
  Encounter,
  DocumentReference,
  ChargeItem,
  Invoice,
} from '@medplum/fhirtypes';
import { useCallback, useEffect, useState } from 'react';
import type { SavedPaymentMethod } from '@/lib/stripeBillingService';
import { listPaymentMethods } from '@/lib/stripeBillingService';
import { searchCoveragesForPatient } from '../../../lib/patientCoverageSearch';
import { patientContactToContact, relatedPersonToContact } from '../utils/clientProfileFhir';
import { resolveContactsWithRoi } from '../utils/contactRoiUtils';
import { CONSENT_CATEGORY_SYSTEM, ROI_CONSENT_CATEGORY_CODE, INSURANCE_AUTHORIZATION_CODE, INSURANCE_AUTHORIZATION_CODE_SYSTEM } from '@unify/fhir-model/constants';
import type { Contact } from '../data/contactTypes';
import type { InsuranceAuthorizationRecord } from '@/lib/billing/insuranceAuthorization';
import { basicToAuthorization } from '@/lib/billing/insuranceAuthorization';
import type { InsuranceCardDocument } from '@/lib/billing/insuranceCards';
import { documentToInsuranceCard } from '@/lib/billing/insuranceCards';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ClientProfileBillingData {
  paymentMethods: SavedPaymentMethod[];
  coverages: Coverage[];
  eligibilityResponses: CoverageEligibilityResponse[];
  encounters: Encounter[];
  chargeItems: ChargeItem[];
  invoices: Invoice[];
  insuranceCards: InsuranceCardDocument[];
  authorizations: InsuranceAuthorizationRecord[];
  statements: DocumentReference[];
}

export interface ClientProfileData {
  patient: Patient | null;
  managingOrganization: Organization | null;
  contacts: Contact[];
  relatedPersons: RelatedPerson[];
  billing: ClientProfileBillingData;
  appointments: { first?: Appointment; last?: Appointment };
  activeEpisode: EpisodeOfCare | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useClientProfileData(patientId: string | undefined): ClientProfileData {
  const medplum = useMedplum();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [managingOrganization, setManagingOrganization] = useState<Organization | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [relatedPersons, setRelatedPersons] = useState<RelatedPerson[]>([]);
  const [billing, setBilling] = useState<ClientProfileBillingData>({
    paymentMethods: [],
    coverages: [],
    eligibilityResponses: [],
    encounters: [],
    chargeItems: [],
    invoices: [],
    insuranceCards: [],
    authorizations: [],
    statements: [],
  });
  const [appointments, setAppointments] = useState<{ first?: Appointment; last?: Appointment }>({});
  const [activeEpisode, setActiveEpisode] = useState<EpisodeOfCare | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchAll = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const patientRef = `Patient/${patientId}`;

      const emptyRPs: RelatedPerson[] = [];
      const emptyElig: CoverageEligibilityResponse[] = [];
      const emptyEnc: Encounter[] = [];
      const emptyChargeItems: ChargeItem[] = [];
      const emptyInvoices: Invoice[] = [];
      const emptyCardDocs: DocumentReference[] = [];
      const emptyAuthBasics: Basic[] = [];
      const emptyDocs: DocumentReference[] = [];
      const emptyEocs: EpisodeOfCare[] = [];
      const emptyAppts: Appointment[] = [];
      const emptyConsents: Consent[] = [];

      // Patient is critical — the rest degrade gracefully on 403
      const pt = await medplum.readResource('Patient', patientId);

      const [
        rps,
        coverages,
        eligResponses,
        encounters,
        chargeItems,
        invoices,
        cardDocs,
        authBasics,
        statements,
        eocs,
        appts,
        roiConsents,
      ] = await Promise.all([
        medplum.searchResources('RelatedPerson', { patient: patientRef }).catch(() => emptyRPs),
        searchCoveragesForPatient(medplum, patientId),
        medplum.searchResources('CoverageEligibilityResponse', {
          patient: patientRef,
          _sort: '-created',
          _count: '50',
        }).catch(() => emptyElig),
        medplum.searchResources('Encounter', {
          subject: patientRef,
          _sort: '-date',
          _count: '100',
        }).catch(() => emptyEnc),
        medplum.searchResources('ChargeItem', {
          subject: patientRef,
          _sort: '-date',
          _count: '100',
        }).catch(() => emptyChargeItems),
        medplum.searchResources('Invoice', {
          subject: patientRef,
          _sort: '-date',
          _count: '100',
        }).catch(() => emptyInvoices),
        medplum.searchResources('DocumentReference', {
          subject: patientRef,
          _sort: '-date',
          _count: '100',
        }).then((docs) =>
          docs.filter((d) =>
            d.extension?.some((e) =>
              e.valueCode === 'insurance-card-front' || e.valueCode === 'insurance-card-back',
            ),
          ),
        ).catch(() => emptyCardDocs),
        medplum.searchResources('Basic', {
          subject: patientRef,
          code: `${INSURANCE_AUTHORIZATION_CODE_SYSTEM}|${INSURANCE_AUTHORIZATION_CODE}`,
          _sort: '-_lastUpdated',
          _count: '50',
        }).catch(() => emptyAuthBasics),
        medplum.searchResources('DocumentReference', {
          subject: patientRef,
          type: 'billing-statement',
          _sort: '-date',
          _count: '50',
        }).catch(() => emptyDocs),
        medplum.searchResources('EpisodeOfCare', { patient: patientRef, status: 'active' }).catch(() => emptyEocs),
        medplum.searchResources('Appointment', {
          actor: patientRef,
          _sort: 'date',
          _count: '1',
        }).catch(() => emptyAppts),
        medplum.searchResources('Consent', {
          patient: patientRef,
          'category:code': `${CONSENT_CATEGORY_SYSTEM}|${ROI_CONSENT_CATEGORY_CODE}`,
          _sort: '-_lastUpdated',
          _count: '50',
        }).catch(() => emptyConsents),
      ]);

      setPatient(pt);

      // Contacts from Patient.contact + RelatedPerson, enriched with ROI Consents
      const patientContacts = (pt.contact ?? []).map((pc, idx) =>
        patientContactToContact(pc, idx),
      );
      const rpContacts = rps.map((rp) => relatedPersonToContact(rp));
      const allContacts = resolveContactsWithRoi([...patientContacts, ...rpContacts], roiConsents);
      setContacts(allContacts);
      setRelatedPersons(rps);

      const orgRef = pt.managingOrganization?.reference;
      if (orgRef) {
        try {
          const [, orgId] = orgRef.split('/');
          const org = await medplum.readResource('Organization', orgId);
          setManagingOrganization(org);
        } catch {
          setManagingOrganization(null);
        }
      } else {
        setManagingOrganization(null);
      }

      // Active episode
      setActiveEpisode(eocs[0] ?? null);

      // First/last appointment
      let firstAppt: Appointment | undefined;
      let lastAppt: Appointment | undefined;
      if (appts.length > 0) {
        firstAppt = appts[0];
        const lastAppts = await medplum.searchResources('Appointment', {
          actor: patientRef,
          _sort: '-date',
          _count: '1',
        });
        lastAppt = lastAppts[0];
      }
      setAppointments({ first: firstAppt, last: lastAppt });

      // Payment methods via Stripe bot
      let paymentMethods: SavedPaymentMethod[] = [];
      try {
        paymentMethods = await listPaymentMethods(medplum, patientId);
      } catch {
        // Stripe bot may not be configured; gracefully degrade
      }

      setBilling({
        paymentMethods,
        coverages,
        eligibilityResponses: eligResponses,
        encounters,
        chargeItems,
        invoices,
        insuranceCards: cardDocs.map(documentToInsuranceCard).filter((c): c is InsuranceCardDocument => c != null),
        authorizations: authBasics.map(basicToAuthorization).filter((a): a is InsuranceAuthorizationRecord => a != null),
        statements,
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [medplum, patientId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return {
    patient,
    managingOrganization,
    contacts,
    relatedPersons,
    billing,
    appointments,
    activeEpisode,
    loading,
    error,
    refresh: fetchAll,
  };
}
